import { Input } from "@/crud/form/Input";
import { Quill } from "@/crud/form/Quill/Quill";
import { useFormStore } from "@/store/form";
import { Formik, useFormikContext } from "formik";
import { InfoIcon, SaveIcon } from "lucide-react";
import { useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { BASE_URL } from "@/dashboardQueries";
import { FormSettings } from "./FormSettings";
import { FormSettingsInput } from "@/crud/form/FormSettingsInput";

export const FormEndingSettings = () => {
  const { values } = useFormikContext();
  const { form, activeFormEnding } = useFormStore();
  const queryClient = useQueryClient();

  const mode = values.mode.id;

  // Update form ending mutation
  const updateFormEnding = useMutation(
    async (updatedEnding: any) => {
      const response = await axios.put(
        `${BASE_URL}/dashboard/form-endings/${updatedEnding.id}`,
        updatedEnding
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("formEndings"); // Invalidate the query to refresh the list
      },
    }
  );

  // Find all dropdown fields that have isCorrect to track how many correct answers are available
  const dropdownFields = values.fields.filter(
    (field: any) =>
      field.field.type === "multipleChoice" || field.field.type === "dropdown"
  );
  const correctAnswersCount = dropdownFields.reduce(
    (count: number, field: any) => {
      const correctAnswers = field.options.filter(
        (option: any) => option.isCorrect
      );
      return count + correctAnswers.length;
    },
    0
  );

  return (
    <div>
      <label
        htmlFor="project-name"
        className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-300 mb-2"
      >
        Ending Settings
      </label>

      {activeFormEnding && (
        <Formik
          initialValues={activeFormEnding}
          onSubmit={(values, { setSubmitting }) => {
            updateFormEnding.mutate(values, {
              onSettled: () => {
                setSubmitting(false); // Ensure isSubmitting is reset after the mutation
              },
            });
          }}
          enableReinitialize
          validationSchema={null}
        >
          {({
            handleSubmit,
            handleChange,
            handleBlur,
            values,
            setFieldValue,
            isSubmitting,
          }) => (
            <>
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <label
                    htmlFor="name"
                    className="block text-xs font-medium leading-6 text-gray-900 sm:mt-1.5 dark:text-white mb-1"
                  >
                    Name
                  </label>
                  <div>
                    <p className="block text-xs font-medium leading-6 text-gray-900 sm:mt-1.5 dark:text-white/40 mb-1">
                      Optional
                    </p>
                  </div>
                </div>
                <Input
                  name="name"
                  required={false}
                  type="text"
                  value={values.name}
                />
              </div>

              {activeFormEnding.endingType === "redirect" ? (
                <div className="">
                  <label
                    htmlFor="redirectUrl"
                    className="block text-xs font-medium leading-6 text-gray-900 sm:mt-1.5 dark:text-white mb-1"
                  >
                    Redirect URL
                  </label>
                  <FormSettingsInput
                    name="redirectUrl"
                    required={false}
                    type="text"
                    value={values.redirectUrl}
                    onChange={handleChange}
                    // todo: add the variables from the flow
                    variables={{
                      "Context Variables": ["individual_id"],
                      "Form Variables": [],
                    }}
                  />
                </div>
              ) : (
                <div className="">
                  <label
                    htmlFor="content"
                    className="block text-xs font-medium leading-6 text-gray-900 sm:mt-1.5 dark:text-white mb-2"
                  >
                    Content
                  </label>
                  <Quill
                    name="content"
                    onChange={(content: string) =>
                      setFieldValue("content", content)
                    }
                  />
                </div>
              )}

              {mode === "quiz" && (
                <div className="mt-5 border-t dark:border-white/5 pt-3">
                  <div className="flex mb-3">
                    <InfoIcon className="h-6 w-6 text-gray-500 dark:text-white/40 mr-2" />
                    <p className="text-sm text-gray-500 dark:text-white/40">
                      There are a total of{" "}
                      <span className="border dark:border-white/10 mr-1 rounded-lg px-1.5 dark:bg-white/5">
                        {correctAnswersCount}
                      </span>
                      correct answers in this quiz
                    </p>
                  </div>
                  <label
                    htmlFor="content"
                    className="block text-xs font-medium leading-6 text-gray-900 sm:mt-1.5 dark:text-white mb-2"
                  >
                    Show ending for scores between
                  </label>

                  <div className="flex">
                    <div className="">
                      <Input
                        name="minScore"
                        required={false}
                        type="number"
                        value={values.minScore?.toString()}
                      />
                    </div>
                    <div className="mx-2 flex items-center dark:text-white/40 text-xs">
                      <p>and</p>
                    </div>
                    <div className="">
                      <Input
                        name="maxScore"
                        required={false}
                        type="number"
                        value={values.maxScore?.toString()}
                      />
                    </div>
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={() => handleSubmit()}
                className="mt-4 rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin mr-1.5 h-5 w-5 dark:text-gray-900"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>Save</>
                )}
                <SaveIcon className="ml-1 w-4 h-4" />
              </button>
            </>
          )}
        </Formik>
      )}
    </div>
  );
};
